import {log} from '@vanguard/logger'
import TagManager from 'react-gtm-module'
import {api} from '../../../services/api'
import {
  Client,
  Person,
  Feature,
  FeatureSettings,
  UserPermissions,
} from '../../../types'
import {TokenResult} from './TokenService'

export interface SessionData {
  client: Client
  person: Person
  features: Feature[]
  featureSettings: FeatureSettings
  userPermissions: UserPermissions
}

export type SessionInitResult = {
  status: 'success' | 'error'
  data?: SessionData
  error?: Error
}

/**
 * Initialize user session by loading all required data
 * This includes user data, client data, features, and permissions
 */
export async function initializeSession(
  tokenResult: TokenResult,
): Promise<SessionInitResult> {
  try {
    const {clientID, personID} = tokenResult

    const [
      personResponse,
      clientResponse,
      featuresResponse,
      featureSettingsResponse,
      userPermissionsResponse,
    ] = await Promise.all([
      api.persons.getPerson({
        clientID,
        personID,
      }),
      api.clients.getClient({
        clientID,
      }),
      api.featureSettings.fetchFeatures({
        pageNumber: 1,
        pageSize: 100,
      }),
      api.featureSettings.getFeatureSettings({
        clientID,
      }),
      api.permissions.getUserPermissions({
        clientID,
        personID,
      }),
    ])

    if (
      personResponse.result === 'success' &&
      personResponse.data &&
      clientResponse.result === 'success' &&
      clientResponse.data &&
      featuresResponse.result === 'success' &&
      featuresResponse.data &&
      featureSettingsResponse.result === 'success' &&
      featureSettingsResponse.data &&
      userPermissionsResponse.result === 'success' &&
      userPermissionsResponse.data
    ) {
      // Update GTM data layer with user context
      TagManager.dataLayer({
        dataLayer: {
          personID: personResponse.data.ID,
          firstName: personResponse.data.name.first,
          lastName: personResponse.data.name.last,
          email: personResponse.data.email,
          clientID: clientResponse.data.ID,
          campName: clientResponse.data.name,
          salesforceAccountID: clientResponse.data.salesforceAccountID,
        },
      })

      return {
        status: 'success',
        data: {
          person: personResponse.data,
          client: clientResponse.data,
          features: featuresResponse.data.results,
          featureSettings: featureSettingsResponse.data,
          userPermissions: userPermissionsResponse.data,
        },
      }
    }

    const error = new Error('Failed to load one or more required data sets')
    log.error(error)
    return {status: 'error', error}
  } catch (err) {
    const error = err instanceof Error ? err : new Error(JSON.stringify(err))
    log.error(error)
    return {status: 'error', error}
  }
}
